import CircularLoader, {
  CircularLoaderType,
} from '@allesauto/core/circularLoader';
import Gallery from '@allesauto/core/gallery';
import TextLink from '@allesauto/core/textLink';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { PaymentType } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { useAccessoryDetails } from '@allesauto/utils/apiHooks';
import { ToastifyType } from '@allesauto/utils/consts';
import { ChevronLeft16 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';

import FooterPrice from '@/components/OverlayFormFooter/OverlayFooterPrice';
import Footer from '@/components/OverlayFormFooter/OverlayFormFooter';

import { useAddToCart } from '@/global/hooks/shoppingCart';

interface AccessoriesDetailsProps {
  productId: string;
  backToCatalog?: () => void;
  showDealerPriceOverlay?: (
    productId: string,
    accessoriesDealerProviderId: string | undefined
  ) => void;
  accessoriesDealerProviderId?: string;
}

const AccessoriesDetails: FC<
  React.PropsWithChildren<AccessoriesDetailsProps>
> = ({
  productId,
  backToCatalog,
  showDealerPriceOverlay,
  accessoriesDealerProviderId,
}) => {
  const intl = useIntl();
  const { loading: addLoading } = useAddToCart();
  const whiteLabel = useWhiteLabel();

  const { result, loading, error } = useAccessoryDetails({
    dealerId: accessoriesDealerProviderId || '',
    id: productId,
  });

  useEffect(() => {
    if (error) {
      import('@allesauto/core/toastify').then((module) => {
        module.toastifyFactory(
          ToastifyType.ERROR,
          translate(intl, 'global.ERRORS.ACCESSORIES.HEADER'),
          translate(intl, 'global.ERRORS.ACCESSORIES.TEXT')
        );
      });
    }
  }, [intl, error]);

  const className = {
    container: 'h-full',
    backButton: 'mb-40px',
    loader: 'flex h-1/2 items-center justify-center',
    imageGallery: 'w-full',
    title:
      'font-headline text-headline3 mb-10px md:mb-20px mt-15px md:mt-40px text-black',
    description: 'text-grey60 text-p1 font-regular pb-[160px]',
    footerButtonIcon: clsx('mr-10px', whiteLabel ? 'text-black' : 'text-white'),
  };

  const carouselClassName = {
    containerClass: '!pb-0 !mt-0 !col-start-1 !col-end-25 lg:!relative',
    sectionClass: clsx(
      (result?.images.length ?? 0) > 1 && 'sm:!pb-65px md:!pb-[95px]'
    ),
    thumbnailClass:
      '!col-start-1 !col-end-25 !pl-0 lg:!pl-0 md:!pl-0 lg:!pb-0 !pb-0 !mb-0 hide-scrollbar',
    buttonLeftClass: 'ml-20px',
    buttonRightClass: 'mr-20px',
  };

  if (loading) {
    return (
      <div className={className.loader}>
        <CircularLoader type={CircularLoaderType.secondary} />
      </div>
    );
  }

  if (!result) {
    return null;
  }

  return (
    <div className={className.container}>
      {backToCatalog && (
        <div className={className.backButton}>
          <TextLink
            text={translate(
              intl,
              'ui.SHOPPING_CART.ACCESSORIES.BACK_TO_CATALOG'
            )}
            icon={<ChevronLeft16 />}
            onClick={() => !addLoading && backToCatalog()}
          />
        </div>
      )}

      <div className={className.imageGallery}>
        <Gallery
          items={result.images.map((i: any) => ({
            externalUrlSmall: i,
            externalUrlLarge: i,
          }))}
          pictureClassNames="w-full"
          carouselClassNames={carouselClassName}
        />
      </div>
      <h3 className={className.title}>{result.title}</h3>
      <p
        className={className.description}
        dangerouslySetInnerHTML={{ __html: result.fullDescription }}
      />
      <Footer
        buttonText={translate(intl, 'ui.PDP.FOOTER.NEXT_GARAGE_OVERLAY')}
        showButtonLoader={addLoading}
        onButtonClick={() => {
          if (!addLoading) {
            if (showDealerPriceOverlay) {
              showDealerPriceOverlay(productId, accessoriesDealerProviderId);
            }
          }
        }}
      >
        <FooterPrice
          customText={translate(intl, 'ui.PRICE.VAT_INCLUDED')}
          paymentType={PaymentType.CashRange}
          amount={result.price}
        />
      </Footer>
    </div>
  );
};

export default AccessoriesDetails;
